import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, Clock, Home, Heart, ArrowLeft } from 'lucide-react';
import { confirmMercadoPago } from '../lib/api';

const DonationReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  // MP returns these query params: status, payment_id, preference_id, external_reference, collection_id, etc.
  const status = searchParams.get('status') || 'unknown';
  const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');
  const externalRef = searchParams.get('external_reference');
  const preferenceId = searchParams.get('preference_id');

  useEffect(() => {
    if (!externalRef) {
      setLoading(false);
      setResult({ status: 'error', message: 'No se pudo identificar la donación' });
      return;
    }

    confirmMercadoPago({
      payment_id: paymentId,
      status,
      external_reference: externalRef,
      preference_id: preferenceId,
    })
      .then((res) => setResult(res.data))
      .catch(() => setResult({ status: 'error' }))
      .finally(() => setLoading(false));
  }, [externalRef, paymentId, status, preferenceId]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Procesando tu donación...</h2>
          <p className="text-gray-600">Estamos verificando el pago con Mercado Pago</p>
        </div>
      );
    }

    const s = result?.status || 'error';

    if (s === 'verified') {
      return (
        <div className="text-center py-8" data-testid="return-success">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-14 h-14 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">¡Pago Acreditado!</h2>
          <p className="text-lg text-gray-600 mb-2">
            Donación de <span className="font-bold text-sky-600">${(result.amount || 0).toLocaleString('es-AR')}</span>
          </p>
          {result.donor_name && (
            <p className="text-gray-500 mb-6">A nombre de {result.donor_name}</p>
          )}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 max-w-md mx-auto">
            <p className="text-green-800 text-sm flex items-center justify-center">
              <Heart className="w-4 h-4 mr-2 fill-green-600 text-green-600" />
              <strong>¡Muchas gracias por tu aporte!</strong>
            </p>
            <p className="text-green-700 text-sm mt-1">
              Tu donación ayuda a más jóvenes deportistas a cumplir sus sueños.
            </p>
          </div>
        </div>
      );
    }

    if (s === 'confirmed' || (s === 'pending' && paymentId)) {
      return (
        <div className="text-center py-8" data-testid="return-pending">
          <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-14 h-14 text-amber-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Pago en Proceso</h2>
          <p className="text-lg text-gray-600 mb-6">
            Tu pago está siendo procesado por Mercado Pago. Te avisaremos cuando se acredite.
          </p>
        </div>
      );
    }

    if (s === 'rejected') {
      return (
        <div className="text-center py-8" data-testid="return-rejected">
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-14 h-14 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Pago Rechazado</h2>
          <p className="text-lg text-gray-600 mb-6">
            No pudimos procesar tu pago. Probá nuevamente con otro método o por transferencia bancaria.
          </p>
        </div>
      );
    }

    return (
      <div className="text-center py-8" data-testid="return-error">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock className="w-14 h-14 text-gray-500" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Estado pendiente de confirmación</h2>
        <p className="text-lg text-gray-600 mb-6">
          Volvé a intentar o contactanos si tenés dudas sobre tu donación.
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <button
          onClick={() => navigate('/')}
          className="text-sky-700 hover:text-sky-800 mb-6 flex items-center space-x-2 transition-colors"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver al sitio</span>
        </button>

        <Card className="p-8 sm:p-12 shadow-2xl border-0">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3 shadow-lg">
              FD
            </div>
            <p className="text-sm font-semibold text-sky-700">FUNDACIÓN FUTUROS DEL DEPORTE</p>
          </div>

          {renderContent()}

          {!loading && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
              <Button
                onClick={() => navigate('/')}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="goto-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
              <Button
                onClick={() => navigate('/#donar')}
                variant="outline"
                className="border-sky-300 text-sky-700 hover:bg-sky-50"
              >
                Hacer otra donación
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DonationReturn;
